import { DocumentTextIcon } from "@heroicons/react/outline";
import { FileIcon, Maximize2 } from "lucide-react";
import { useState } from "react";

import MiniEditor from "./MiniEditor";
import { getDisplayValue } from "./utils";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Variable } from "@/components/InputVariableConfigurator/types";

interface VariableValueInputProps {
  variable: Variable;
  onChange: (variable: Variable) => void;
  isFileContent: boolean;
  isProcessingFile: boolean;
  onMaximize: () => void;
  tooltip?: string;
  readOnly?: boolean;
  className?: string;
  placeholder?: string;
  isMiniEditorOpen?: boolean;
  setIsMiniEditorOpen?: (open: boolean) => void;
  onFocusNext?: () => void;
  popoverPortalContainer?: HTMLElement | null;
}

const VariableValueInput = ({
  variable,
  onChange,
  isFileContent,
  isProcessingFile,
  onMaximize,
  tooltip = "Variable value",
  readOnly = false,
  className,
  placeholder,
  isMiniEditorOpen: isMiniEditorOpenProp,
  setIsMiniEditorOpen: setIsMiniEditorOpenProp,
  onFocusNext,
  popoverPortalContainer,
}: VariableValueInputProps) => {
  const [isMiniEditorOpenInternal, setIsMiniEditorOpenInternal] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isMiniEditorOpen =
    isMiniEditorOpenProp !== undefined ? isMiniEditorOpenProp : isMiniEditorOpenInternal;
  const setIsMiniEditorOpen =
    setIsMiniEditorOpenProp !== undefined ? setIsMiniEditorOpenProp : setIsMiniEditorOpenInternal;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...variable, value: e.target.value });
  };

  const handleInputClick = () => {
    if (!readOnly) {
      setIsMiniEditorOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && !readOnly) {
      e.preventDefault();
      setIsMiniEditorOpen(true);
    }
  };

  const handleExpandToMaximize = () => {
    setIsMiniEditorOpen(false);
    onMaximize();
  };

  if (isProcessingFile) {
    return <div className="animate-pulse rounded-md bg-gray-100 p-2" />;
  }

  const displayValue = getDisplayValue(variable.value);
  const firstLine = displayValue.split("\n")[0];

  return (
    <div className="relative flex-1">
      <MiniEditor
        isOpen={isMiniEditorOpen}
        onOpenChange={setIsMiniEditorOpen}
        onExpand={handleExpandToMaximize}
        variable={variable}
        onChange={onChange}
        readOnly={readOnly}
        onFocusNext={onFocusNext}
        popoverPortalContainer={popoverPortalContainer}
      >
        <div className="relative w-full">
          <BasicTooltip content={tooltip} usePortal={false}>
            {isFileContent ? (
              <FileIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            ) : (
              <DocumentTextIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            )}
          </BasicTooltip>
          <BasicTooltip content="Maximize editor" usePortal={false}>
            <Button
              variant="ghost"
              size="tinyIcon"
              className="absolute right-2 top-1 z-10"
              onClick={e => {
                e.stopPropagation();
                handleExpandToMaximize();
              }}
              tabIndex={-1}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </BasicTooltip>
          <Input
            type="text"
            placeholder={placeholder || "Click to edit"}
            readOnly
            className={`min-w-[150px] cursor-pointer truncate pl-9 pr-8 font-mono transition-colors hover:bg-gray-50 ${
              isMiniEditorOpen ? "border-2 border-blue-500" : ""
            } ${className}`}
            value={firstLine}
            onClick={handleInputClick}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
        </div>
      </MiniEditor>
    </div>
  );
};

export default VariableValueInput;
